import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { AppSettings } from '../../../services/settings/app-settings.service';
import { AuthService } from '../../../services/auth/auth.service';
import { Router } from '@angular/router';
import { PasskeySummary, TotpEnrollment, TotpSetupStep } from './profile-security.models';

type ProfileSection = 'security';

interface ProfileMenuItem {
  id: ProfileSection;
  title: string;
  description: string;
  icon: string;
}

@Component({
  selector: 'app-security-profile',
  templateUrl: './security-profile.component.html',
  styleUrl: './security-profile.component.scss',
  standalone: false,
})
export class SecurityProfileComponent implements OnInit {
  loading = true;
  registeringPasskey = false;
  deletingCredentialId: string | null = null;
  updatingPasskeyCredentialId: string | null = null;
  changingPassword = false;
  savingProfile = false;
  resettingSessions = false;
  showEmailDialog = false;
  showPasswordDialog = false;
  showPasskeyNameDialog = false;
  showDeletePasskeyDialog = false;
  totpEnabled = false;
  loadingTwoFactor = false;
  startingTotpEnrollment = false;
  confirmingTotpEnrollment = false;
  disablingTotp = false;
  showTotpSetupDialog = false;
  showTotpDisableDialog = false;
  totpSetupStep: TotpSetupStep = 'password';
  totpEnrollment: TotpEnrollment | null = null;
  activeSection: ProfileSection = 'security';
  securityProfile: any = null;
  editingPasskey: PasskeySummary | null = null;
  deletingPasskey: PasskeySummary | null = null;
  profileForm: FormGroup;
  passwordForm: FormGroup;
  passkeyNameForm: FormGroup;
  deletePasskeyForm: FormGroup;
  totpPasswordForm: FormGroup;
  totpVerifyForm: FormGroup;
  totpDisableForm: FormGroup;
  readonly profileMenuItems: ProfileMenuItem[] = [
    {
      id: 'security',
      title: 'Seguridad',
      description: 'Administra preferencias y configuraciones de seguridad',
      icon: 'pi pi-shield',
    },
  ];

  constructor(
    private readonly appSettings: AppSettings,
    public readonly authService: AuthService,
    private readonly formBuilder: FormBuilder,
    private readonly messageService: MessageService,
    private readonly router: Router
  ) {
    this.appSettings.appEmpty.set(false);
    this.appSettings.appHideGlobalHeader.set(false);

    this.profileForm = this.formBuilder.group({
      picture: [''],
      givenName: ['', [Validators.required, Validators.minLength(2)]],
      familyName: [''],
      location: [''],
      email: ['', [Validators.required, Validators.email]],
    });

    this.passwordForm = this.formBuilder.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(12), Validators.pattern(/^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).+$/)]],
      confirmPassword: ['', Validators.required],
    });

    this.passkeyNameForm = this.formBuilder.group({
      label: ['', [Validators.required, Validators.minLength(3)]],
    });

    this.deletePasskeyForm = this.formBuilder.group({
      currentPassword: ['', Validators.required],
    });

    this.totpPasswordForm = this.formBuilder.group({
      currentPassword: ['', Validators.required],
    });

    this.totpVerifyForm = this.formBuilder.group({
      deviceName: [''],
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });

    this.totpDisableForm = this.formBuilder.group({
      currentPassword: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    void this.loadSecurityProfile();
  }

  get passkeys(): PasskeySummary[] {
    return Array.isArray(this.securityProfile?.passkeys) ? this.securityProfile.passkeys : [];
  }

  get displayName(): string {
    const givenName = this.profileForm.value.givenName || this.securityProfile?.givenName || '';
    const familyName = this.profileForm.value.familyName || this.securityProfile?.familyName || '';
    return [givenName, familyName].filter(Boolean).join(' ') || this.securityProfile?.name || this.securityProfile?.email || 'Usuario Metabridge';
  }

  get profileInitials(): string {
    const source = this.displayName || this.securityProfile?.email || 'MB';
    const parts = source.split(/\s+/).filter(Boolean);
    const initials = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : source.slice(0, 2);
    return initials.toUpperCase();
  }

  get profileMenu(): MenuItem[] {
    return [
      {
        label: 'Mi Perfil',
        items: this.profileMenuItems.map((item) => ({
          label: item.title,
          icon: item.icon,
          styleClass: this.activeSection === item.id ? 'p-focus' : undefined,
          command: () => this.setActiveSection(item.id),
        })),
      },
    ];
  }

  get activeMenuItem(): ProfileMenuItem {
    return this.profileMenuItems.find((item) => item.id === this.activeSection) || this.profileMenuItems[0];
  }

  get supportsPasskeys(): boolean {
    return this.authService.supportsPasskeys();
  }

  async loadSecurityProfile(): Promise<void> {
    try {
      this.loading = true;
      this.loadingTwoFactor = true;
      const twoFactorRequest =
        typeof (this.authService as any).getTwoFactorStatus === 'function' ? this.authService.getTwoFactorStatus().catch(() => null) : Promise.resolve(null);
      const [profile, twoFactorStatus] = await Promise.all([this.authService.getSecurityProfile(), twoFactorRequest]);
      this.securityProfile = profile;
      this.totpEnabled = twoFactorStatus?.totpEnabled === true || this.securityProfile?.twoFactor?.totpEnabled === true;
      this.hydrateProfileForm(this.securityProfile);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo cargar el perfil',
        detail: 'Intenta nuevamente en unos segundos.',
        life: 5000,
      });
    } finally {
      this.loadingTwoFactor = false;
      this.loading = false;
    }
  }

  setActiveSection(section: ProfileSection): void {
    this.activeSection = section;
  }

  openEmailDialog(): void {
    this.profileForm.patchValue({ email: this.securityProfile?.email || this.profileForm.value.email || '' });
    this.showEmailDialog = true;
  }

  closeEmailDialog(): void {
    this.showEmailDialog = false;
    this.profileForm.patchValue({ email: this.securityProfile?.email || '' });
  }

  openPasswordDialog(): void {
    this.passwordForm.reset();
    this.showPasswordDialog = true;
  }

  closePasswordDialog(): void {
    this.showPasswordDialog = false;
    this.passwordForm.reset();
  }

  async saveProfile(): Promise<void> {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    try {
      this.savingProfile = true;
      const profile = await this.authService.updateSecurityProfile({
        picture: this.profileForm.value.picture || undefined,
        givenName: this.profileForm.value.givenName,
        familyName: this.profileForm.value.familyName || undefined,
        location: this.profileForm.value.location || undefined,
        email: this.profileForm.value.email,
      });
      this.securityProfile = profile;
      this.hydrateProfileForm(profile);
      this.showEmailDialog = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Perfil actualizado',
        detail: 'Tus datos fueron guardados correctamente.',
        life: 4500,
      });
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo actualizar',
        detail: error?.response?.data?.message || 'Revisa los datos e intenta nuevamente.',
        life: 5000,
      });
    } finally {
      this.savingProfile = false;
    }
  }

  async registerPasskey(): Promise<void> {
    try {
      this.registeringPasskey = true;
      const result = await this.authService.registerPasskey(this.randomPasskeyLabel());
      this.securityProfile = {
        ...this.securityProfile,
        passkeys: result.passkeys,
        passkeysEnrolled: result.passkeys?.length ?? 0,
        passkeyRequired: (result.passkeys?.length ?? 0) > 0,
      };
      this.messageService.add({
        severity: 'success',
        summary: 'Passkey registrado',
        detail: 'Se usará como segundo factor en futuros inicios de sesión.',
        life: 5000,
      });
    } catch (error: any) {
      const detail = error?.message === 'PASSKEY_NOT_SUPPORTED' ? 'Este navegador o dispositivo no soporta passkeys.' : 'No se pudo registrar el passkey.';
      this.messageService.add({
        severity: 'error',
        summary: 'Error al registrar passkey',
        detail,
        life: 5000,
      });
    } finally {
      this.registeringPasskey = false;
    }
  }

  async deletePasskey(passkey: PasskeySummary): Promise<void> {
    this.deletingPasskey = passkey;
    this.deletePasskeyForm.reset();
    this.showDeletePasskeyDialog = true;
  }

  closeDeletePasskeyDialog(): void {
    if (this.deletingCredentialId) return;
    this.showDeletePasskeyDialog = false;
    this.deletingPasskey = null;
    this.deletePasskeyForm.reset();
  }

  async confirmDeletePasskey(): Promise<void> {
    if (!this.deletingPasskey || this.deletePasskeyForm.invalid) {
      this.deletePasskeyForm.markAllAsTouched();
      return;
    }

    try {
      this.deletingCredentialId = this.deletingPasskey.credentialId;
      const result = await this.authService.deletePasskey(this.deletingPasskey.credentialId, this.deletePasskeyForm.value.currentPassword);
      this.securityProfile = {
        ...this.securityProfile,
        passkeys: result.passkeys,
        passkeysEnrolled: result.passkeysEnrolled,
        passkeyRequired: result.passkeysEnrolled > 0,
      };
      this.showDeletePasskeyDialog = false;
      this.deletingPasskey = null;
      this.deletePasskeyForm.reset();
      this.messageService.add({
        severity: 'success',
        summary: 'Passkey eliminado',
        detail: 'El dispositivo ya no podrá usarse como segundo factor.',
        life: 4500,
      });
    } catch (error: any) {
      const status = error?.response?.status;
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo eliminar',
        detail: status === 401 ? 'La contraseña actual no es correcta.' : 'Intenta nuevamente o contacta soporte.',
        life: 5000,
      });
    } finally {
      this.deletingCredentialId = null;
    }
  }

  openPasskeyNameDialog(passkey: PasskeySummary): void {
    this.editingPasskey = passkey;
    this.passkeyNameForm.patchValue({ label: passkey.label || '' });
    this.showPasskeyNameDialog = true;
  }

  closePasskeyNameDialog(): void {
    this.showPasskeyNameDialog = false;
    this.editingPasskey = null;
    this.passkeyNameForm.reset();
  }

  async savePasskeyName(): Promise<void> {
    if (!this.editingPasskey || this.passkeyNameForm.invalid) {
      this.passkeyNameForm.markAllAsTouched();
      return;
    }

    try {
      this.updatingPasskeyCredentialId = this.editingPasskey.credentialId;
      const result = await this.authService.updatePasskey(this.editingPasskey.credentialId, this.passkeyNameForm.value.label);
      this.securityProfile = {
        ...this.securityProfile,
        passkeys: result.passkeys,
        passkeysEnrolled: result.passkeysEnrolled,
        passkeyRequired: result.passkeysEnrolled > 0,
      };
      this.messageService.add({
        severity: 'success',
        summary: 'Passkey actualizado',
        detail: 'El nombre del passkey fue actualizado correctamente.',
        life: 4000,
      });
      this.closePasskeyNameDialog();
    } catch (error: any) {
      const detail =
        error?.message === 'PASSKEY_NOT_SUPPORTED'
          ? 'Este navegador o dispositivo no soporta passkeys.'
          : error?.message === 'PASSKEY_OPTIONS_NOT_AVAILABLE'
            ? 'No se pudo iniciar la validación con passkey.'
            : 'Valida tu passkey e intenta nuevamente.';
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo actualizar',
        detail,
        life: 5000,
      });
    } finally {
      this.updatingPasskeyCredentialId = null;
    }
  }

  async changePassword(): Promise<void> {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    const { currentPassword, newPassword, confirmPassword } = this.passwordForm.value;
    if (newPassword !== confirmPassword) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Contraseñas distintas',
        detail: 'La nueva contraseña y la confirmación deben coincidir.',
        life: 4500,
      });
      return;
    }

    try {
      this.changingPassword = true;
      await this.authService.changePassword(currentPassword, newPassword);
      this.passwordForm.reset();
      this.showPasswordDialog = false;
      this.messageService.add({
        severity: 'success',
        summary: 'Contraseña actualizada',
        detail: 'Tu contraseña fue cambiada correctamente.',
        life: 5000,
      });
    } catch (error: any) {
      const status = error?.response?.status;
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo cambiar',
        detail: status === 401 ? 'La contraseña actual no es correcta.' : 'Revisa los datos e intenta nuevamente.',
        life: 5000,
      });
    } finally {
      this.changingPassword = false;
    }
  }

  openTotpSetupDialog(): void {
    this.totpPasswordForm.reset();
    this.totpVerifyForm.reset({ deviceName: '', code: '' });
    this.totpEnrollment = null;
    this.totpSetupStep = 'password';
    this.showTotpSetupDialog = true;
  }

  closeTotpSetupDialog(): void {
    if (this.startingTotpEnrollment || this.confirmingTotpEnrollment) return;
    this.showTotpSetupDialog = false;
    this.totpEnrollment = null;
    this.totpSetupStep = 'password';
    this.totpPasswordForm.reset();
    this.totpVerifyForm.reset({ deviceName: '', code: '' });
  }

  async startTotpEnrollment(): Promise<void> {
    if (this.totpPasswordForm.invalid) {
      this.totpPasswordForm.markAllAsTouched();
      return;
    }

    try {
      this.startingTotpEnrollment = true;
      this.totpEnrollment = await this.authService.startTotpEnrollment(this.totpPasswordForm.value.currentPassword);
      this.totpSetupStep = 'qr';
      this.totpPasswordForm.reset();
    } catch (error: any) {
      const status = error?.response?.status;
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo iniciar la configuración',
        detail: status === 401 ? 'La contraseña actual no es correcta.' : error?.response?.data?.message || 'Intenta nuevamente o contacta soporte.',
        life: 5000,
      });
    } finally {
      this.startingTotpEnrollment = false;
    }
  }

  async confirmTotpEnrollment(): Promise<void> {
    if (!this.totpEnrollment || this.totpVerifyForm.invalid) {
      this.totpVerifyForm.markAllAsTouched();
      return;
    }

    try {
      this.confirmingTotpEnrollment = true;
      const result = await this.authService.confirmTotpEnrollment(
        this.totpEnrollment.setupId,
        this.totpVerifyForm.value.code,
        this.totpVerifyForm.value.deviceName
      );
      this.totpEnabled = result?.totpEnabled === true;
      this.showTotpSetupDialog = false;
      this.totpEnrollment = null;
      this.totpSetupStep = 'password';
      this.totpVerifyForm.reset({ deviceName: '', code: '' });
      this.messageService.add({
        severity: 'success',
        summary: 'Autenticación en dos pasos activada',
        detail: 'A partir del próximo inicio de sesión te pediremos el código de tu app.',
        life: 5000,
      });
    } catch (error: any) {
      const status = error?.response?.status;
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo activar',
        detail:
          status === 401
            ? 'El código ingresado no es válido. Verifica la hora de tu dispositivo e intenta nuevamente.'
            : error?.response?.data?.message || 'Intenta nuevamente o contacta soporte.',
        life: 5000,
      });
    } finally {
      this.confirmingTotpEnrollment = false;
    }
  }

  /**
   * El diálogo avanza de contraseña -> QR -> código. Las etapas posteriores a la
   * primera solo existen mientras haya un enrolamiento vigente.
   */
  setTotpSetupStep(step: TotpSetupStep): void {
    if (step !== 'password' && !this.totpEnrollment) return;
    this.totpSetupStep = step;
  }

  openTotpDisableDialog(): void {
    this.totpDisableForm.reset();
    this.showTotpDisableDialog = true;
  }

  closeTotpDisableDialog(): void {
    if (this.disablingTotp) return;
    this.showTotpDisableDialog = false;
    this.totpDisableForm.reset();
  }

  async disableTotp(): Promise<void> {
    if (this.totpDisableForm.invalid) {
      this.totpDisableForm.markAllAsTouched();
      return;
    }

    try {
      this.disablingTotp = true;
      const result = await this.authService.disableTotp(this.totpDisableForm.value.currentPassword);
      this.totpEnabled = result?.totpEnabled === true;
      this.showTotpDisableDialog = false;
      this.totpDisableForm.reset();
      this.messageService.add({
        severity: 'success',
        summary: 'Autenticación en dos pasos desactivada',
        detail: 'Tu cuenta ya no solicitará el código de la app autenticadora.',
        life: 4500,
      });
    } catch (error: any) {
      const status = error?.response?.status;
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo desactivar',
        detail: status === 401 ? 'La contraseña actual no es correcta.' : 'Intenta nuevamente o contacta soporte.',
        life: 5000,
      });
    } finally {
      this.disablingTotp = false;
    }
  }

  async copyTotpSecret(secret: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(secret);
      this.messageService.add({
        severity: 'success',
        summary: 'Clave copiada',
        detail: 'Pégala en tu app autenticadora como clave basada en tiempo.',
        life: 3500,
      });
    } catch {
      this.messageService.add({
        severity: 'warn',
        summary: 'No se pudo copiar',
        detail: 'Copia la clave manualmente desde el campo.',
        life: 4000,
      });
    }
  }

  async resetAllSessions(): Promise<void> {
    const confirmed = window.confirm('¿Cerrar todas tus sesiones activas? Tendrás que iniciar sesión nuevamente en este y otros dispositivos.');
    if (!confirmed) return;

    try {
      this.resettingSessions = true;
      await this.authService.resetSessions();
      this.authService.clearSession();
      this.messageService.add({
        severity: 'success',
        summary: 'Sesiones cerradas',
        detail: 'Se cerraron las sesiones remotas. Inicia sesión nuevamente.',
        life: 3500,
      });
      void this.router.navigate(['/login'], { queryParams: { reason: 'session_reset' } });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'No se pudo cerrar sesiones',
        detail: 'Intenta nuevamente o contacta soporte.',
        life: 5000,
      });
    } finally {
      this.resettingSessions = false;
    }
  }

  formatDate(value?: string): string {
    if (!value) return '—';
    try {
      return new Intl.DateTimeFormat('es-CL', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value));
    } catch {
      return value;
    }
  }

  private randomPasskeyLabel(): string {
    try {
      const platform = navigator.platform || 'dispositivo';
      const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
      return `Passkey ${platform} ${suffix}`;
    } catch {
      return `Passkey ${Date.now().toString().slice(-4)}`;
    }
  }

  private hydrateProfileForm(profile: any): void {
    const splitName = this.splitName(profile?.name || '');
    this.profileForm.patchValue({
      picture: profile?.picture || '',
      givenName: profile?.givenName || splitName.givenName || '',
      familyName: profile?.familyName || splitName.familyName || '',
      location: profile?.location || '',
      email: profile?.email || '',
    });
  }

  private splitName(name: string): { givenName: string; familyName: string } {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { givenName: '', familyName: '' };
    return {
      givenName: parts[0],
      familyName: parts.slice(1).join(' '),
    };
  }
}
